import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from './index';

// Query Keys
export const userQueryKeys = {
  all: ['users'],
  lists: () => [...userQueryKeys.all, 'list'],
  list: (filters) => [...userQueryKeys.lists(), { filters }],
  details: () => [...userQueryKeys.all, 'detail'],
  detail: (id) => [...userQueryKeys.details(), id],
  profile: () => [...userQueryKeys.all, 'profile'],
};

// Queries
export const useUsers = (filters = {}) => {
  return useQuery({
    queryKey: userQueryKeys.list(filters),
    queryFn: () => userService.getAllUsers(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUser = (userId) => {
  return useQuery({
    queryKey: userQueryKeys.detail(userId),
    queryFn: () => userService.getUserById(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useUserProfile = () => {
  return useQuery({
    queryKey: userQueryKeys.profile(),
    queryFn: () => userService.getUserProfile(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Mutations
export const useRegisterUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData) => userService.registerUser(userData),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
    },
    onError: (error) => {
      console.error('Registration failed:', error);
    },
  });
};

export const useLoginUser = () => {
  return useMutation({
    mutationFn: (credentials) => userService.loginUser(credentials),
    onSuccess: (data) => {
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('authToken', data.token);
      }
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationFn: (payload) => userService.forgotPassword(payload),
    onError: (error) => {
      console.error('Forgot password failed:', error);
    },
  });
};

export const useVerifyOtp = () => {
  return useMutation({
    mutationFn: (payload) => userService.verifyOtp(payload),
    onError: (error) => {
      console.error('OTP verification failed:', error);
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: (payload) => userService.resetPassword(payload),
    onError: (error) => {
      console.error('Password reset failed:', error);
    },
  });
};

export const useGoogleAuth = () => {
  return useMutation({
    mutationFn: (googleData) => userService.googleAuth(googleData),
    onSuccess: (data) => {
      if (data.token) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('authToken', data.token);
      }
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }
    },
    onError: (error) => {
      console.error('Google authentication failed:', error);
    },
  });
};

export const useSubmitKYCDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, kycData }) => userService.submitKYCDetails(userId, kycData),
    onSuccess: (data, variables) => {
      const { userId } = variables;

      // Update the specific user in cache
      queryClient.setQueryData(userQueryKeys.detail(userId), data);

      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });

      // Update profile if it's the current user
      queryClient.invalidateQueries({ queryKey: userQueryKeys.profile() });
    },
    onError: (error) => {
      console.error('KYC submission failed:', error);
    },
  });
};

export const useSubmitBankDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, bankData }) => userService.submitBankDetails(userId, bankData),
    onSuccess: (data, variables) => {
      const { userId } = variables;

      // Update the specific user in cache
      queryClient.setQueryData(userQueryKeys.detail(userId), data);

      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });

      // Update profile if it's the current user
      queryClient.invalidateQueries({ queryKey: userQueryKeys.profile() });
    },
    onError: (error) => {
      console.error('Bank details submission failed:', error);
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, userData }) => userService.updateUser(userId, userData),
    onSuccess: (data, variables) => {
      const { userId } = variables;

      // Update the specific user in cache
      queryClient.setQueryData(userQueryKeys.detail(userId), data);

      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });

      // Update profile if it's the current user
      queryClient.invalidateQueries({ queryKey: userQueryKeys.profile() });
    },
    onError: (error) => {
      console.error('User update failed:', error);
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId) => userService.deleteUser(userId),
    onSuccess: (data, userId) => {
      // Remove user from cache
      queryClient.removeQueries({ queryKey: userQueryKeys.detail(userId) });

      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
    },
    onError: (error) => {
      console.error('User deletion failed:', error);
    },
  });
};

// Combined hook for user management operations
export const useUserManagement = () => {
  const usersQuery = useUsers();
  const registerMutation = useRegisterUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();

  return {
    // Queries
    users: usersQuery.data || [],
    isLoading: usersQuery.isLoading,
    error: usersQuery.error,

    // Mutations
    registerUser: registerMutation.mutateAsync,
    updateUser: updateMutation.mutateAsync,
    deleteUser: deleteMutation.mutateAsync,

    // Mutation states
    isRegistering: registerMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Refetch function
    refetch: usersQuery.refetch,
  };
};
